import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DownloadSimple, Table, MagnifyingGlass, Funnel, ChartLine, TrendUp, CurrencyDollar, Clock } from '@phosphor-icons/react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const DAY_NAMES = ['D','L','M','M','J','V','S'];

const STATE_STYLES = {
  T: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Trabajo' },
  D: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Descanso' },
  I: { bg: 'bg-red-100', text: 'text-red-800', label: 'Inasistencia' },
  V: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Vacaciones' },
  L: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Licencia' },
  P: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Permiso' },
  F: { bg: 'bg-zinc-200', text: 'text-zinc-700', label: 'Feriado' },
  Z: { bg: 'bg-zinc-100', text: 'text-zinc-300', label: 'No contratado' },
};

const BRANCH_COLORS = ['#18181b', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const formatCLP = (v) => `$${(v || 0).toLocaleString('es-CL')}`;

const ReportsPage = () => {
  const { axiosInstance, isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportType, setReportType] = useState('dashboard');
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [branchFilter, setBranchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cargoFilter, setCargoFilter] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [sheetData, setSheetData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Dashboard state
  const [dashYear, setDashYear] = useState(new Date().getFullYear());
  const [dashBranch, setDashBranch] = useState('');
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [branchesRes, cargosRes] = await Promise.all([
          axiosInstance.get('/api/branches'),
          axiosInstance.get('/api/cargos'),
        ]);
        setBranches(branchesRes.data);
        setCargos(cargosRes.data.filter(c => c.is_active));
      } catch (error) { /* silenced */ }
    };
    loadFilters();
  }, [axiosInstance]);

  const loadDashboard = useCallback(async () => {
    try {
      setDashLoading(true);
      const params = { year: dashYear };
      if (dashBranch) params.branch_id = dashBranch;
      const res = await axiosInstance.get('/api/report/dashboard-anual', { params });
      setDashData(res.data);
    } catch (e) { toast.error('Error al cargar dashboard'); }
    finally { setDashLoading(false); }
  }, [axiosInstance, dashYear, dashBranch]);

  useEffect(() => { if (reportType === 'dashboard') loadDashboard(); }, [reportType, loadDashboard]);

  const handlePreview = async () => {
    if (reportType !== 'branch-sheet') return;
    setLoading(true);
    setShowPreview(false);
    try {
      const [y, m] = selectedMonth.split('-');
      const params = { year: y, month: m };
      if (branchFilter) params.branch_id = branchFilter;
      if (statusFilter) params.status = statusFilter;
      if (cargoFilter) params.cargo = cargoFilter;
      const res = await axiosInstance.get('/api/report/branch-monthly-sheet', { params });
      setSheetData(res.data);
      setShowPreview(true);
    } catch (error) { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let url = '';
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      if (branchFilter) params.append('branch_id', branchFilter);

      if (reportType === 'advances') {
        url = '/api/export/advances';
        params.append('date_from', dateFrom);
        params.append('date_to', dateTo);
      } else if (reportType === 'monthly') {
        url = '/api/export/monthly-summary';
        params.append('month', selectedMonth);
      } else {
        url = '/api/export/branch-monthly-sheet';
        const [y, m] = selectedMonth.split('-');
        params.append('year', y);
        params.append('month', m);
        if (statusFilter) params.append('status', statusFilter);
        if (cargoFilter) params.append('cargo', cargoFilter);
      }

      const response = await axiosInstance.get(`${url}?${params.toString()}`, { responseType: 'blob' });
      const filenames = {
        'advances': `anticipos_${dateFrom}_${dateTo}.${exportFormat}`,
        'monthly': `resumen_mensual_${selectedMonth}.${exportFormat}`,
        'branch-sheet': `planilla_sucursal_${selectedMonth}.${exportFormat}`,
      };
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filenames[reportType]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte exportado');
    } catch (error) { toast.error('Error al exportar reporte'); }
    finally { setExporting(false); }
  };

  const getDow = (year, month, day) => new Date(year, month - 1, day).getDay();
  const isWeekend = (year, month, day) => { const d = getDow(year, month, day); return d === 0 || d === 6; };

  // Prepare chart data
  const chartOvertimeData = dashData?.months?.map(m => {
    const entry = { name: m.month_name, Total: m.total_overtime };
    dashData.branches?.forEach(b => { entry[b.name] = m.by_branch?.[b.id]?.overtime || 0; });
    return entry;
  }) || [];

  const chartAdvancesData = dashData?.months?.map(m => {
    const entry = { name: m.month_name, Total: m.total_advances };
    dashData.branches?.forEach(b => { entry[b.name] = m.by_branch?.[b.id]?.advances || 0; });
    return entry;
  }) || [];

  // Current month summary
  const currentMonthIdx = new Date().getMonth();
  const currentMonthData = dashData?.months?.[currentMonthIdx];

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="reports-title">Reportes</h1>
        <p className="text-sm text-zinc-500 mt-1">Dashboard y exportacion de datos</p>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { id: 'dashboard', title: 'Dashboard Anual', desc: 'Graficos de horas extras y anticipos por mes', icon: ChartLine },
          { id: 'branch-sheet', title: 'Planilla por Sucursal', desc: 'Control de asistencia diaria', icon: Table },
          { id: 'advances', title: 'Detalle Anticipos', desc: 'Listado por rango de fechas', icon: CurrencyDollar },
          { id: 'monthly', title: 'Resumen Mensual', desc: 'Consolidado por trabajador', icon: DownloadSimple },
        ].map(rt => (
          <button
            key={rt.id}
            onClick={() => { setReportType(rt.id); setShowPreview(false); }}
            className={`p-4 border text-left transition-colors ${
              reportType === rt.id ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
            }`}
            data-testid={`report-type-${rt.id}`}
          >
            <rt.icon size={20} className="text-zinc-500 mb-2" />
            <h3 className="font-heading font-semibold text-zinc-900 text-sm">{rt.title}</h3>
            <p className="text-[10px] text-zinc-500 mt-1">{rt.desc}</p>
          </button>
        ))}
      </div>

      {/* ─── DASHBOARD TAB ─── */}
      {reportType === 'dashboard' && (
        <div className="space-y-5" data-testid="dashboard-section">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end bg-white border border-zinc-200 p-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Ano</label>
              <select value={dashYear} onChange={e => setDashYear(Number(e.target.value))} className="form-select text-sm w-28" data-testid="dash-year">
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {isAdmin && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1">Sucursal</label>
                <select value={dashBranch} onChange={e => setDashBranch(e.target.value)} className="form-select text-sm w-44" data-testid="dash-branch">
                  <option value="">Todos los locales</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {dashLoading ? (
            <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
          ) : dashData ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-zinc-200 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">HH.EE mes actual</div>
                  <div className="text-2xl font-bold text-zinc-900 mt-1 font-mono">{currentMonthData?.total_overtime || 0}h</div>
                  <div className="text-[10px] text-zinc-400">{MONTHS[currentMonthIdx]} {dashYear}</div>
                </div>
                <div className="bg-white border border-zinc-200 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Anticipos mes actual</div>
                  <div className="text-2xl font-bold text-zinc-900 mt-1">{formatCLP(currentMonthData?.total_advances)}</div>
                  <div className="text-[10px] text-zinc-400">{currentMonthData?.total_advance_count || 0} anticipos</div>
                </div>
                <div className="bg-white border border-zinc-200 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">HH.EE acumulado {dashYear}</div>
                  <div className="text-2xl font-bold text-zinc-900 mt-1 font-mono">{dashData.months.reduce((s, m) => s + m.total_overtime, 0)}h</div>
                </div>
                <div className="bg-white border border-zinc-200 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Anticipos acumulado {dashYear}</div>
                  <div className="text-2xl font-bold text-zinc-900 mt-1">{formatCLP(dashData.months.reduce((s, m) => s + m.total_advances, 0))}</div>
                </div>
              </div>

              {/* Overtime Chart */}
              <div className="bg-white border border-zinc-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={18} className="text-blue-500" />
                  <h3 className="text-sm font-bold text-zinc-900">Horas Extras por Mes — {dashYear}</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartOvertimeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#71717a' }} />
                    <Tooltip
                      contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0, fontSize: 12 }}
                      formatter={(value) => [`${value}h`, '']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {!dashBranch && dashData.branches?.length > 1 ? (
                      dashData.branches.map((b, i) => (
                        <Bar key={b.id} dataKey={b.name} fill={BRANCH_COLORS[i % BRANCH_COLORS.length]} stackId="a" />
                      ))
                    ) : (
                      <Bar dataKey="Total" fill="#18181b" />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Advances Chart */}
              <div className="bg-white border border-zinc-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CurrencyDollar size={18} className="text-emerald-500" />
                  <h3 className="text-sm font-bold text-zinc-900">Anticipos por Mes — {dashYear}</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartAdvancesData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#71717a' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0, fontSize: 12 }}
                      formatter={(value) => [formatCLP(value), '']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {!dashBranch && dashData.branches?.length > 1 ? (
                      dashData.branches.map((b, i) => (
                        <Line key={b.id} type="monotone" dataKey={b.name} stroke={BRANCH_COLORS[i % BRANCH_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                      ))
                    ) : (
                      <Line type="monotone" dataKey="Total" stroke="#18181b" strokeWidth={2} dot={{ r: 4 }} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Monthly Detail Table */}
              <div className="bg-white border border-zinc-200 overflow-x-auto">
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                  <TrendUp size={18} className="text-zinc-500" />
                  <h3 className="text-sm font-bold text-zinc-900">Detalle Mensual {dashYear}</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-100">
                      <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Mes</th>
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">HH.EE</th>
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">Anticipos ($)</th>
                      <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">N Anticipos</th>
                      {!dashBranch && dashData.branches?.map(b => (
                        <th key={b.id} className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500">{b.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashData.months.map(m => (
                      <tr key={m.month} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                        <td className="px-4 py-2 text-sm font-medium text-zinc-900">{MONTHS[m.month - 1]}</td>
                        <td className="px-4 py-2 text-center font-mono text-xs font-bold text-blue-700">{m.total_overtime > 0 ? `${m.total_overtime}h` : '-'}</td>
                        <td className="px-4 py-2 text-center font-mono text-xs font-bold text-emerald-700">{m.total_advances > 0 ? formatCLP(m.total_advances) : '-'}</td>
                        <td className="px-4 py-2 text-center text-xs text-zinc-500">{m.total_advance_count || '-'}</td>
                        {!dashBranch && dashData.branches?.map(b => {
                          const bd = m.by_branch?.[b.id];
                          return (
                            <td key={b.id} className="px-3 py-2 text-center text-[10px]">
                              {bd?.overtime > 0 || bd?.advances > 0 ? (
                                <div>
                                  {bd.overtime > 0 && <span className="text-blue-600 font-mono font-bold">{bd.overtime}h</span>}
                                  {bd.overtime > 0 && bd.advances > 0 && <span className="text-zinc-300 mx-1">|</span>}
                                  {bd.advances > 0 && <span className="text-emerald-600">{formatCLP(bd.advances)}</span>}
                                </div>
                              ) : <span className="text-zinc-300">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="bg-zinc-50 font-bold border-t-2 border-zinc-300">
                      <td className="px-4 py-2 text-sm text-zinc-900">Total {dashYear}</td>
                      <td className="px-4 py-2 text-center font-mono text-xs text-blue-800">{dashData.months.reduce((s, m) => s + m.total_overtime, 0)}h</td>
                      <td className="px-4 py-2 text-center font-mono text-xs text-emerald-800">{formatCLP(dashData.months.reduce((s, m) => s + m.total_advances, 0))}</td>
                      <td className="px-4 py-2 text-center text-xs text-zinc-600">{dashData.months.reduce((s, m) => s + m.total_advance_count, 0)}</td>
                      {!dashBranch && dashData.branches?.map(b => {
                        const totOt = dashData.months.reduce((s, m) => s + (m.by_branch?.[b.id]?.overtime || 0), 0);
                        const totAdv = dashData.months.reduce((s, m) => s + (m.by_branch?.[b.id]?.advances || 0), 0);
                        return (
                          <td key={b.id} className="px-3 py-2 text-center text-[10px]">
                            <span className="text-blue-700 font-mono">{totOt}h</span>
                            <span className="text-zinc-300 mx-1">|</span>
                            <span className="text-emerald-700">{formatCLP(totAdv)}</span>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ─── EXPORT TABS ─── */}
      {reportType !== 'dashboard' && (
        <>
          {/* Filters */}
          <div className="border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Funnel size={18} className="text-zinc-600" />
              <h3 className="font-heading font-semibold text-zinc-900 text-sm">Filtros</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {reportType === 'advances' ? (
                <>
                  <div>
                    <label className="form-label">Desde</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input" data-testid="report-date-from" />
                  </div>
                  <div>
                    <label className="form-label">Hasta</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input" data-testid="report-date-to" />
                  </div>
                </>
              ) : (
                <div>
                  <label className="form-label">Mes</label>
                  <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="form-input" data-testid="report-month" />
                </div>
              )}
              {isAdmin && (
                <div>
                  <label className="form-label">Sucursal</label>
                  <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} className="form-select" data-testid="report-branch-filter">
                    <option value="">Todas</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              {reportType === 'branch-sheet' && (
                <>
                  <div>
                    <label className="form-label">Estado</label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-select" data-testid="report-status-filter">
                      <option value="">Todos</option>
                      <option value="activo">Activo</option>
                      <option value="desvinculado">Desvinculado</option>
                      <option value="vacaciones">Vacaciones</option>
                      <option value="con licencia">Con Licencia</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Cargo</label>
                    <select value={cargoFilter} onChange={e => setCargoFilter(e.target.value)} className="form-select" data-testid="report-cargo-filter">
                      <option value="">Todos</option>
                      {cargos.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="form-label">Formato</label>
                <select value={exportFormat} onChange={e => setExportFormat(e.target.value)} className="form-select" data-testid="report-format">
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {reportType === 'branch-sheet' && (
              <button onClick={handlePreview} disabled={loading} className="btn-secondary flex items-center gap-2" data-testid="preview-report-btn">
                {loading ? <div className="spinner"></div> : <MagnifyingGlass size={18} />}
                {loading ? 'Cargando...' : 'Vista Previa'}
              </button>
            )}
            <button onClick={handleExport} disabled={exporting} className="btn-primary flex items-center gap-2" data-testid="export-report-btn">
              {exporting ? <div className="spinner"></div> : <DownloadSimple size={18} />}
              {exporting ? 'Generando...' : 'Exportar'}
            </button>
          </div>

          {/* Legend */}
          {reportType === 'branch-sheet' && (
            <div className="flex flex-wrap gap-3 text-xs">
              {Object.entries(STATE_STYLES).map(([key, style]) => (
                <div key={key} className="flex items-center gap-1">
                  <span className={`w-6 h-6 flex items-center justify-center ${style.bg} ${style.text} font-mono font-bold`}>{key}</span>
                  <span className="text-zinc-600">{style.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Preview Table */}
          {showPreview && sheetData && (
            <div className="border border-zinc-200 bg-white" data-testid="sheet-preview">
              <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Table size={18} className="text-zinc-600" />
                  <h3 className="font-heading font-semibold text-zinc-900 text-sm">
                    Planilla {MONTHS[sheetData.month - 1]} {sheetData.year} — {sheetData.branch_name}
                  </h3>
                </div>
                <span className="text-xs text-zinc-500">{sheetData.rows.length} trabajadores</span>
              </div>
              <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 420px)' }}>
                <table className="w-full border-collapse text-xs" data-testid="sheet-table">
                  <thead className="sticky top-0 z-10 bg-zinc-900 text-white">
                    <tr>
                      <th className="sticky left-0 z-20 bg-zinc-900 px-2 py-2 text-left font-medium w-24 min-w-[96px]">Cargo</th>
                      <th className="sticky left-24 z-20 bg-zinc-900 px-2 py-2 text-left font-medium w-28 min-w-[112px]">Apellido</th>
                      <th className="sticky left-52 z-20 bg-zinc-900 px-2 py-2 text-left font-medium w-24 min-w-[96px]">Nombre</th>
                      {Array.from({ length: sheetData.days_in_month }, (_, i) => {
                        const day = i + 1;
                        const we = isWeekend(sheetData.year, sheetData.month, day);
                        const dow = getDow(sheetData.year, sheetData.month, day);
                        return (
                          <th key={day} className={`px-0.5 py-1 text-center font-medium w-9 min-w-[36px] ${we ? 'bg-zinc-800' : ''}`}>
                            <div>{day}</div>
                            <div className="text-[9px] font-normal opacity-60">{DAY_NAMES[dow]}</div>
                          </th>
                        );
                      })}
                      <th className="px-2 py-2 text-center font-medium bg-zinc-800 w-14 min-w-[56px]">Dias T.</th>
                      <th className="px-2 py-2 text-center font-medium bg-zinc-800 w-14 min-w-[56px]">HH.EE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheetData.rows.map((row) => (
                      <tr key={row.id} className={`border-b border-zinc-100 ${row.status === 'desvinculado' ? 'opacity-50' : ''}`}>
                        <td className="sticky left-0 z-10 bg-white px-2 py-1.5 text-zinc-600 border-r border-zinc-100 truncate w-24 min-w-[96px]">{row.cargo}</td>
                        <td className="sticky left-24 z-10 bg-white px-2 py-1.5 font-medium text-zinc-900 border-r border-zinc-100 truncate w-28 min-w-[112px]">{row.apellido} {row.segundo_apellido}</td>
                        <td className="sticky left-52 z-10 bg-white px-2 py-1.5 text-zinc-700 border-r border-zinc-100 truncate w-24 min-w-[96px]">{row.nombre}</td>
                        {row.days.map((val, i) => {
                          const style = STATE_STYLES[val];
                          const we = isWeekend(sheetData.year, sheetData.month, i + 1);
                          return (
                            <td key={`day-${i + 1}`} className={`text-center border-r border-zinc-100 w-9 min-w-[36px] ${style ? `${style.bg} ${style.text} font-bold` : we ? 'bg-zinc-50' : ''}`}>{val}</td>
                          );
                        })}
                        <td className="text-center font-bold text-zinc-900 border-r border-zinc-100 bg-zinc-50">{row.days_worked || '-'}</td>
                        <td className="text-center font-bold text-sky-700 bg-sky-50">{row.overtime_hours > 0 ? row.overtime_hours : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Report Description */}
          {reportType !== 'branch-sheet' && (
            <div className="border border-zinc-200 bg-white p-5">
              <h3 className="font-heading font-semibold text-zinc-900 mb-3 text-sm">
                {reportType === 'advances' ? 'Contenido del Reporte de Anticipos' : 'Contenido del Resumen Mensual'}
              </h3>
              {reportType === 'advances' ? (
                <ul className="list-disc list-inside text-sm text-zinc-600 space-y-1">
                  <li>Fecha del anticipo</li><li>Codigo del trabajador</li><li>Nombre completo</li><li>Sucursal</li><li>Monto en CLP</li><li>Observaciones</li><li>Usuario que registro</li>
                </ul>
              ) : (
                <ul className="list-disc list-inside text-sm text-zinc-600 space-y-1">
                  <li>Codigo del trabajador</li><li>Nombre completo</li><li>Sucursal</li><li>Total de anticipos del mes (CLP)</li><li>Cantidad de anticipos recibidos</li><li>Detalle de fechas</li>
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
